/**
 * RealtimeContext - Contexto global para Realtime unificado
 * 
 * OTIMIZAÇÃO v3.0 - INTENT-DRIVEN REALTIME:
 * - Canal único por unidade: `operador_${unidadeOperadorId}`
 * - Distribui eventos internamente para os hooks
 * - Evita múltiplas conexões WebSocket
 * - Fornece status de conexão + fallback polling
 * - NOVO: Integra EventBus para emitir db:confirmado/db:erro
 * - NOVO: Reconexão com backoff exponencial (5s base)
 * 
 * PRINCÍPIO: Banco é CONFIRMAÇÃO, não gatilho de UI.
 * O EventBus recebe eventos do banco apenas para reconciliação.
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { RealtimeChannel, RealtimePostgresChangesPayload } from '@supabase/supabase-js';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { devLog, devError } from '@/lib/logger';
import { logWSStatus, logEvent, startThrottleDetection } from '@/debug/realtimeDebug';
import { eventBus } from '@/lib/eventBus';
import { eventReconciler } from '@/lib/eventReconciler';
import { isDirectPatchTable, isIntentFirstTable } from '@/lib/realtimeConfig';

type RealtimeStatus = 'disconnected' | 'connecting' | 'subscribed' | 'error';

// Tabelas monitoradas pelo canal unificado
// NOTA: mensagens_cliente foi removido - usa subscription direta no hook
export type RealtimeTable = 
  | 'pedidos' 
  | 'fiados' 
  | 'fiado_pagamentos'
  | 'clientes' 
  | 'fluxo_caixa' 
  | 'avaliacoes'
  | 'lembretes'
  | 'metas'
  | 'cofre'
  | 'fidelidade_config'
  | 'fidelidade_pontos'
  | 'fidelidade_resgates'
  | 'cardapio'
  | 'checkins'
  | 'depositos_meta'
  | 'clube_metricas'
  | 'clube_pontos'
  | 'clube_ranking'
  | 'solicitacoes_cancelamento'
  | 'solicitacoes_despesa'
  | 'avisos'
  | 'avisos_lidos'
  | 'avisos_usuario'
  | 'unidade_config'
  | 'atividades_log'
  | 'estoque_diario'
  | 'vendas_produto'
  | 'ocorrencias'
  | 'checkin_feedbacks'
  | 'despesas_recorrentes'
  | 'contatos_whatsapp';

type RealtimeEventHandler = (payload: RealtimePostgresChangesPayload<Record<string, unknown>>) => void;

interface RealtimeContextValue {
  status: RealtimeStatus;
  isConnected: boolean;
  lastError: string | null;
  lastStatusChange: Date | null;
  reconnect: () => void;
  /** Se true, hooks devem usar polling como fallback */
  shouldUseFallback: boolean;
  /** Alias para shouldUseFallback */
  isFallbackMode: boolean;
  /** ID da unidade atual (para referência) */
  unidadeId: number | null;
  /** Registrar listener para uma tabela específica */
  subscribe: (table: RealtimeTable, handler: RealtimeEventHandler) => () => void;
}

const RealtimeContext = createContext<RealtimeContextValue | undefined>(undefined);

const FALLBACK_THRESHOLD_MS = 10000; // 10 segundos sem conexão = ativar fallback

// Tabelas que serão monitoradas no canal unificado
// NOTA: mensagens_cliente REMOVIDO - usa subscription direta em useMensagensCliente
const MONITORED_TABLES: RealtimeTable[] = [
  'pedidos',
  'fiados',
  'fiado_pagamentos',
  'clientes',
  'fluxo_caixa',
  'avaliacoes',
  'lembretes',
  'metas',
  'cofre',
  'fidelidade_config',
  'fidelidade_pontos',
  'fidelidade_resgates',
  'cardapio',
  'checkins',
  'depositos_meta',
  'clube_metricas',
  'clube_pontos',
  'clube_ranking',
  'solicitacoes_cancelamento',
  'solicitacoes_despesa',
  'avisos',
  'avisos_lidos',
  'avisos_usuario',
  'unidade_config',
  'atividades_log',
  'estoque_diario',
  'vendas_produto',
  'ocorrencias',
  'checkin_feedbacks',
  'despesas_recorrentes',
  'contatos_whatsapp',
];

// Tabelas que usam is_same_unidade() no RLS ou filtros via relacionamento
// Não usar filtro user_id=eq.X pois inclui múltiplos user_ids ou usa outro campo
const UNIDADE_WIDE_TABLES: RealtimeTable[] = [
  'lembretes',
  'ocorrencias',
  'checkin_feedbacks', // Filtra por checkin_id (via checkins.user_id), não tem user_id próprio
];

interface RealtimeProviderProps {
  children: React.ReactNode;
  unidadeOperadorId: number | null;
}

export const RealtimeProvider: React.FC<RealtimeProviderProps> = ({ children, unidadeOperadorId }) => {
  const [status, setStatus] = useState<RealtimeStatus>('disconnected');
  const [lastError, setLastError] = useState<string | null>(null);
  const [lastStatusChange, setLastStatusChange] = useState<Date | null>(null);
  const [shouldUseFallback, setShouldUseFallback] = useState(false);
  
  const queryClient = useQueryClient();
  const channelRef = useRef<RealtimeChannel | null>(null);
  const fallbackTimerRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectTimerRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef = useRef<number>(0);
  const previousStatusRef = useRef<RealtimeStatus>('disconnected');
  
  // Map de handlers por tabela
  const handlersRef = useRef<Map<RealtimeTable, Set<RealtimeEventHandler>>>(new Map());

  const updateStatus = useCallback((newStatus: RealtimeStatus, error?: string) => {
    const previousStatus = previousStatusRef.current;
    previousStatusRef.current = newStatus;
    
    setStatus(newStatus);
    setLastStatusChange(new Date());
    if (error) setLastError(error);
    
    // [RT DEBUG] Log status change
    logWSStatus(newStatus, error);
    
    // ============= RESILIÊNCIA: Invalidar cache ao reconectar =============
    // Quando a conexão é restabelecida após estar desconectada,
    // forçar refetch de dados críticos para garantir sincronismo
    if (newStatus === 'subscribed' && (previousStatus === 'disconnected' || previousStatus === 'error')) {
      devLog('[RealtimeContext]', '🔄 Conexão restabelecida - invalidando cache crítico');
      
      // Invalidar queries críticas para forçar refetch
      queryClient.invalidateQueries({ queryKey: ['pedidos'] });
      queryClient.invalidateQueries({ queryKey: ['cross-sell-profit'] });
      queryClient.invalidateQueries({ queryKey: ['vendas-produto'] });
      queryClient.invalidateQueries({ queryKey: ['cardapio'] });
      queryClient.invalidateQueries({ queryKey: ['clientes'] });
      queryClient.invalidateQueries({ queryKey: ['fiados'] });
      queryClient.invalidateQueries({ queryKey: ['fluxo_caixa'] });
      queryClient.invalidateQueries({ queryKey: ['whatsapp-instance-config'] });
      queryClient.invalidateQueries({ queryKey: ['whatsapp-connection-status'] });
      
      // Notificar usuário
      toast.success('Conexão restabelecida', {
        description: 'Dados sincronizados',
        duration: 3000,
      });
    }
    
    // Gerenciar fallback timer
    if (newStatus === 'subscribed') {
      setShouldUseFallback(false);
      if (fallbackTimerRef.current) {
        clearTimeout(fallbackTimerRef.current);
        fallbackTimerRef.current = null;
      }
    } else if (newStatus === 'error' || newStatus === 'disconnected') {
      // Iniciar timer para fallback se não estiver conectado
      if (!fallbackTimerRef.current) {
        fallbackTimerRef.current = setTimeout(() => {
          devLog('[RealtimeContext]', '⚠️ Ativando fallback polling');
          logWSStatus('fallback_activated', 'Polling ativado após timeout');
          setShouldUseFallback(true);
        }, FALLBACK_THRESHOLD_MS);
      }
    }
  }, [queryClient]);

  // Handler centralizado de eventos - INTENT-DRIVEN
  const handleRealtimeEvent = useCallback((
    table: RealtimeTable,
    payload: RealtimePostgresChangesPayload<Record<string, unknown>>
  ) => {
    // [RT DEBUG] Log evento recebido
    logEvent(table, { eventType: payload.eventType, new: payload.new, old: payload.old });

    const newData = payload.new as Record<string, unknown> | undefined;
    const realId = newData?.id as number | undefined;

    // ============= INTENT-DRIVEN: Emitir db:confirmado =============
    // O banco confirma operações iniciadas localmente
    // Hooks que usam optimistic updates escutam esses eventos
    
    if (table === 'pedidos' && realId) {
      // Verificar se é duplicata de intenção recente
      if (!eventReconciler.isDbEventDuplicate('pedido', realId)) {
        eventReconciler.markDbProcessed('pedido', realId);
        
        // Emitir evento de confirmação para reconciliação
        if (payload.eventType === 'INSERT') {
          eventBus.emit('db:confirmado', {
            type: 'pedido',
            tempId: 0, // Banco não sabe o tempId
            realId,
          });
        }
      }
    }

    // NOTA: mensagens_cliente agora usa subscription direta no hook
    // Este bloco foi removido para evitar duplicação de eventos
    
    // Notificar handlers registrados (hooks específicos)
    const handlers = handlersRef.current.get(table);
    if (handlers) {
      handlers.forEach(handler => {
        try {
          handler(payload);
        } catch (err) {
          devError('[RealtimeContext]', `Erro no handler de ${table}:`, err);
        }
      });
    }
    
    // ============= INVALIDAÇÃO CONTROLADA (INTENT-DRIVEN) =============
    // 
    // REGRA ARQUITETURAL:
    // 1. Tabelas INTENT-FIRST usam optimistic updates via patch direto no cache.
    //    O banco é CONFIRMAÇÃO, não gatilho de UI.
    // 2. Tabelas DIRECT-PATCH recebem setQueryData direto via useRealtimeQuery.
    //    NÃO devem receber invalidateQueries pois causaria reset do cache.
    //
    // - pedidos: intent-first, optimistic updates em usePedidos.ts
    // - fluxo_caixa, cardapio, etc: direct-patch via useRealtimeQuery
    // - mensagens_cliente: subscription direta em useMensagensCliente.ts
    //
    // Para tabelas intent-first ou direct-patch, pular invalidateQueries.
    //
    if (isIntentFirstTable(table) || isDirectPatchTable(table)) {
      devLog('[RealtimeContext]', `⚡ Tabela ${table} usa patch direto ou é intent-first - sem invalidate`);
    } else {
      // Invalidar apenas tabelas que NÃO usam patch direto
      queryClient.invalidateQueries({ queryKey: [table] });
    }
  }, [queryClient]);

  const connect = useCallback(() => {
    if (!unidadeOperadorId) {
      devLog('[RealtimeContext]', 'Aguardando unidadeOperadorId...');
      return;
    }

    // Limpar channel anterior se existir
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
    }

    updateStatus('connecting');
    devLog('[RealtimeContext]', `🔌 Conectando canal unificado: operador_${unidadeOperadorId}`);

    // Criar canal unificado para a unidade
    let channel = supabase.channel(`operador_${unidadeOperadorId}`);

    // Registrar listeners para todas as tabelas monitoradas
    MONITORED_TABLES.forEach(table => {
      // Tabelas que usam is_same_unidade() não podem usar filtro restrito
      // pois incluem múltiplos user_ids da mesma unidade operacional
      const isUnidadeWide = UNIDADE_WIDE_TABLES.includes(table);
      
      const channelConfig: {
        event: '*';
        schema: 'public';
        table: string;
        filter?: string;
      } = {
        event: '*',
        schema: 'public',
        table,
      };
      
      // Aplicar filtro apenas para tabelas que usam user_id único
      if (!isUnidadeWide) {
        if (table === 'depositos_meta') {
          channelConfig.filter = `unidade_id=eq.${unidadeOperadorId}`;
        } else {
          channelConfig.filter = `user_id=eq.${unidadeOperadorId}`;
        }
      }
      
      channel = channel.on(
        'postgres_changes',
        channelConfig,
        (payload) => handleRealtimeEvent(table, payload)
      );
    });

    // Subscrever ao canal
    channel.subscribe((subscribeStatus, err) => {
      devLog('[RealtimeContext]', 'Status:', subscribeStatus, err?.message || '');
      
      if (subscribeStatus === 'SUBSCRIBED') {
        updateStatus('subscribed');
        devLog('[RealtimeContext]', `✅ Canal unificado ativo: operador_${unidadeOperadorId}`);
      } else if (subscribeStatus === 'CHANNEL_ERROR' || subscribeStatus === 'TIMED_OUT') {
        updateStatus('error', err?.message || 'Falha na conexão');
        
        // INTENT-DRIVEN: Reconexão com backoff exponencial
        // Base: 5 segundos, max: 30 segundos
        if (!reconnectTimerRef.current) {
          const attempt = reconnectAttemptsRef.current;
          const delay = Math.min(5000 * Math.pow(1.5, attempt), 30000);
          reconnectAttemptsRef.current++;
          
          devLog('[RealtimeContext]', `🔄 Reconectando em ${delay/1000}s (tentativa ${attempt + 1})`);
          
          reconnectTimerRef.current = setTimeout(() => {
            reconnectTimerRef.current = null;
            connect();
          }, delay);
        }
      } else if (subscribeStatus === 'CLOSED') {
        updateStatus('disconnected');
      }
    });

    channelRef.current = channel;
  }, [unidadeOperadorId, updateStatus, handleRealtimeEvent]);

  const reconnect = useCallback(() => {
    devLog('[RealtimeContext]', '🔄 Reconexão manual solicitada');
    setLastError(null);
    connect();
  }, [connect]);

  // Função para registrar handlers de tabelas específicas
  const subscribe = useCallback((table: RealtimeTable, handler: RealtimeEventHandler) => {
    if (!handlersRef.current.has(table)) {
      handlersRef.current.set(table, new Set());
    }
    handlersRef.current.get(table)!.add(handler);
    
    // Retorna função de cleanup
    return () => {
      const handlers = handlersRef.current.get(table);
      if (handlers) {
        handlers.delete(handler);
      }
    };
  }, []);

  // Conectar quando unidadeOperadorId estiver disponível
  useEffect(() => {
    let stopThrottleDetection: (() => void) | null = null;
    
    if (unidadeOperadorId) {
      connect();
      // [RT DEBUG] Iniciar detecção de throttle
      stopThrottleDetection = startThrottleDetection();
    }

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
      }
      if (fallbackTimerRef.current) {
        clearTimeout(fallbackTimerRef.current);
      }
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
      }
      if (stopThrottleDetection) {
        stopThrottleDetection();
      }
    };
  }, [unidadeOperadorId, connect]);

  const value = useMemo<RealtimeContextValue>(() => ({
    status,
    isConnected: status === 'subscribed',
    lastError,
    lastStatusChange,
    reconnect,
    shouldUseFallback,
    isFallbackMode: shouldUseFallback,
    unidadeId: unidadeOperadorId,
    subscribe,
  }), [status, lastError, lastStatusChange, reconnect, shouldUseFallback, unidadeOperadorId, subscribe]);

  return (
    <RealtimeContext.Provider value={value}>
      {children}
    </RealtimeContext.Provider>
  );
};

// Wrapper que obtém unidadeOperadorId automaticamente
export const RealtimeProviderWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Usar estado local para evitar re-render infinito
  const [unidadeOperadorId, setUnidadeOperadorId] = useState<number | null>(null);

  useEffect(() => {
    // Buscar unidadeOperadorId do perfil
    const fetchUnidadeId = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data } = await supabase
        .from('users')
        .select('id, operador_id')
        .eq('auth_id', user.id)
        .single();

      if (data) {
        setUnidadeOperadorId(data.operador_id ?? data.id);
      }
    };

    fetchUnidadeId();

    // Listener para mudanças de auth
    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      fetchUnidadeId();
    });

    return () => subscription.unsubscribe();
  }, []);

  return (
    <RealtimeProvider unidadeOperadorId={unidadeOperadorId}>
      {children}
    </RealtimeProvider>
  );
};

export const useRealtimeStatus = (): RealtimeContextValue => {
  const context = useContext(RealtimeContext);
  if (!context) {
    throw new Error('useRealtimeStatus must be used within a RealtimeProvider');
  }
  return context;
};

export default RealtimeContext;
